/**
 * Load lifecycle actions (blueprint §Load progress / §Transitions) on top of
 * the pure step model in @haulsheet/core (loadProgress). Every change is a
 * local Dexie write + outbox mutation, so it works offline and syncs; the
 * server re-checks the transition and sets bookedAt / deliveredAt / paidAt and
 * the settlement.
 *
 * Detention hooks (blueprint: "Rolling arrived/left. Detention row when
 * (departed-arrived) > freeHours"): arriving at a stop starts a detention clock
 * linked to the load and stop (so it shows as running on Loads and Logs);
 * leaving stops it, and if the wait stayed inside free time the row is dropped.
 * A clock the driver already started by hand (not tied to a load) is adopted.
 */
import {
  DocumentKind,
  LoadStatus,
  StopKind,
  canTransition,
  detentionPay,
  newId,
  progressStops,
  type BackStep,
  type Detention,
  type Document,
  type Load,
  type LoadStop,
  type NextStep,
  type ProgressStop,
} from "@haulsheet/core";
import { getDb } from "../db/dexie";
import { departedAtFor, detentionDefaults } from "../logs/detention";
import type { MeResponse } from "../store/authStore";
import { getSyncClient } from "../sync/SyncClient";

export type Actor = Pick<MeResponse, "role" | "driverId"> & { org: { id: string } };

export class LifecycleError extends Error {}

const nowIso = () => new Date().toISOString();

async function freshLoad(id: string): Promise<Load> {
  const l = await getDb().load.get(id);
  if (!l) throw new LifecycleError("This load isn't on this phone anymore.");
  return l;
}

/** Stop rows for the load, creating pickup/drop from origin/destination if it has none. */
export async function ensureStops(load: Load): Promise<LoadStop[]> {
  const db = getDb();
  const existing = (await db.load_stop.where("loadId").equals(load.id).toArray())
    .filter((s) => s.deletedAt == null)
    .sort((a, b) => a.seq - b.seq);
  if (existing.length > 0) return existing;
  const out: LoadStop[] = [];
  for (const s of progressStops(load, [])) {
    const at = nowIso();
    const row: LoadStop = {
      id: newId(),
      orgId: load.orgId,
      version: 0,
      createdAt: at,
      updatedAt: at,
      deletedAt: null,
      loadId: load.id,
      seq: s.seq,
      kind: s.kind,
      place: s.place,
      arrivedAt: null,
      departedAt: null,
    };
    await getSyncClient().mutate({
      entity: "load_stop",
      op: "upsert",
      id: row.id,
      baseVersion: null,
      patch: row as unknown as Record<string, unknown>,
      row: row as unknown as Record<string, unknown>,
    });
    out.push(row);
  }
  return out;
}

async function patchStop(stopId: string, patch: Partial<LoadStop>): Promise<void> {
  const stop = await getDb().load_stop.get(stopId);
  if (!stop) return;
  await getSyncClient().mutate({
    entity: "load_stop",
    op: "upsert",
    id: stop.id,
    baseVersion: stop.version || null,
    patch: patch as Record<string, unknown>,
    row: { ...stop, ...patch, updatedAt: nowIso() } as unknown as Record<string, unknown>,
  });
}

async function hasPod(loadId: string): Promise<boolean> {
  return (await getDb().document.where("loadId").equals(loadId).toArray()).some(
    (d) => d.kind === DocumentKind.pod && d.deletedAt == null,
  );
}

/** Status change with the same checks the server runs; optimistic server fields locally. */
export async function transition(loadId: string, to: LoadStatus, actor: Actor): Promise<void> {
  const load = await freshLoad(loadId);
  if (load.status === to) return;
  const docs = (await getDb().document.where("loadId").equals(loadId).toArray()).filter((d) => d.deletedAt == null);
  const result = canTransition(load, to, { role: actor.role, actorDriverId: actor.driverId, documents: docs });
  if (!result.ok) throw new LifecycleError(result.message);
  const at = nowIso();
  const local: Partial<Load> = { status: to };
  if (result.effects.setBookedAt && !load.bookedAt) local.bookedAt = at;
  if (result.effects.clearBookedAt) local.bookedAt = null;
  if (result.effects.setDeliveredAt && !load.deliveredAt) local.deliveredAt = at;
  if (result.effects.clearDeliveredAt) local.deliveredAt = null;
  if (result.effects.detachSettlement) local.settlementId = null;
  await getSyncClient().mutate({
    entity: "load",
    op: "upsert",
    id: load.id,
    baseVersion: load.version || null,
    // Only status goes to the server; it owns the timestamps and settlement.
    patch: { status: to },
    row: { ...load, ...local, updatedAt: at } as unknown as Record<string, unknown>,
  });
}

// ---- detention hooks ------------------------------------------------------

async function detentionFor(stopId: string): Promise<Detention | undefined> {
  return (await getDb().detention.toArray())
    .filter((d) => d.deletedAt == null && d.loadStopId === stopId)
    .sort((a, b) => b.arrivedAt.localeCompare(a.arrivedAt))[0];
}

async function writeDetention(row: Detention, patch: Partial<Detention>, create = false): Promise<void> {
  const next = { ...row, ...patch, updatedAt: nowIso() };
  await getSyncClient().mutate({
    entity: "detention",
    op: "upsert",
    id: row.id,
    baseVersion: create ? null : row.version || null,
    patch: (create ? next : patch) as unknown as Record<string, unknown>,
    row: next as unknown as Record<string, unknown>,
  });
}

/** An unlinked running clock is adopted by a stop only if it started this recently. */
export const ADOPT_UNLINKED_WITHIN_MS = 30 * 60 * 1000;

/** Arrived at a stop: start (or adopt) the detention clock for it. */
export async function startStopDetention(load: Load, stop: Pick<LoadStop, "id" | "place">, arrivedAt: string, actor: Actor): Promise<void> {
  const db = getDb();
  const all = (await db.detention.toArray()).filter((d) => d.deletedAt == null);
  if (all.some((d) => d.loadStopId === stop.id && d.departedAt == null)) return;
  // A clock started by hand just before arriving belongs to this stop. An
  // older one is something else (another dock, a forgotten clock): leave it
  // running as its own unlinked row and start fresh here.
  const arrivedMs = Date.parse(arrivedAt);
  const manual = all
    .filter(
      (d) =>
        d.departedAt == null &&
        d.loadId == null &&
        Math.abs(arrivedMs - Date.parse(d.arrivedAt)) <= ADOPT_UNLINKED_WITHIN_MS,
    )
    .sort((a, b) => b.arrivedAt.localeCompare(a.arrivedAt))[0];
  if (manual) {
    await writeDetention(manual, { loadId: load.id, loadStopId: stop.id, placeText: manual.placeText ?? (stop.place.text || null) });
    return;
  }
  const { freeHours, rateCentsPerHour } = detentionDefaults(all);
  const row: Detention = {
    id: newId(),
    orgId: actor.org.id,
    version: 0,
    createdAt: arrivedAt,
    updatedAt: arrivedAt,
    deletedAt: null,
    loadId: load.id,
    loadStopId: stop.id,
    placeText: stop.place.text || null,
    arrivedAt,
    departedAt: null,
    freeHours,
    rateCentsPerHour,
    payCents: null,
  };
  await writeDetention(row, {}, true);
}

/** Left a stop: stop its clock; inside free time there's no detention, so the row goes. */
export async function finishStopDetention(stopId: string, departedAt: string): Promise<void> {
  const d = await detentionFor(stopId);
  if (!d || d.departedAt != null) return;
  const left = departedAtFor(d.arrivedAt, new Date(departedAt));
  const pay = detentionPay({ arrivedAt: d.arrivedAt, departedAt: left, freeHours: d.freeHours, rateCentsPerHour: d.rateCentsPerHour });
  if (pay.billableHours > 0) {
    await writeDetention(d, { departedAt: left, payCents: pay.payCents });
  } else {
    await getSyncClient().mutate({ entity: "detention", op: "delete", id: d.id, baseVersion: d.version || null });
  }
}

/** Undo of arriving: the clock that started there goes away. */
async function dropStopDetention(stopId: string): Promise<void> {
  const d = await detentionFor(stopId);
  if (d && d.departedAt == null) {
    await getSyncClient().mutate({ entity: "detention", op: "delete", id: d.id, baseVersion: d.version || null });
  }
}

/** Undo of leaving: the clock runs again from the original arrival. */
async function resumeStopDetention(load: Load, stop: LoadStop, actor: Actor): Promise<void> {
  const d = await detentionFor(stop.id);
  if (d) {
    if (d.departedAt != null) await writeDetention(d, { departedAt: null, payCents: null });
    return;
  }
  if (stop.arrivedAt) await startStopDetention(load, stop, stop.arrivedAt, actor);
}

// ---- steps ----------------------------------------------------------------

async function stopAt(load: Load, index: number): Promise<LoadStop> {
  const stops = await ensureStops(load);
  const s = stops[index];
  if (!s) throw new LifecycleError("That stop is missing.");
  return s;
}

/** Perform the next step shown on load detail. */
export async function doNext(loadId: string, step: NextStep, actor: Actor): Promise<void> {
  const load = await freshLoad(loadId);
  if (step.kind === "need_pod") throw new LifecycleError("Add the POD first.");
  const at = nowIso();
  if (step.kind === "start") {
    // Check the transition before touching stops: a refused start changes nothing.
    const pre = canTransition(load, LoadStatus.rolling, { role: actor.role, actorDriverId: actor.driverId });
    if (!pre.ok) throw new LifecycleError(pre.message);
    const stop = await stopAt(load, step.stop);
    await patchStop(stop.id, { arrivedAt: at });
    await startStopDetention(load, stop, at, actor);
    await transition(loadId, LoadStatus.rolling, actor);
    return;
  }
  const stop = await stopAt(load, step.stop);
  if (step.kind === "arrive") {
    await patchStop(stop.id, { arrivedAt: at });
    await startStopDetention(load, stop, at, actor);
    return;
  }
  // depart
  if (step.delivers) {
    if (!(await hasPod(loadId))) throw new LifecycleError("Add the POD first.");
    const pre = canTransition(load, LoadStatus.delivered, {
      role: actor.role,
      actorDriverId: actor.driverId,
      documents: (await getDb().document.where("loadId").equals(loadId).toArray()).filter((d) => d.deletedAt == null),
    });
    if (!pre.ok) throw new LifecycleError(pre.message);
  }
  const left = stop.arrivedAt ? departedAtFor(stop.arrivedAt, new Date(at)) : at;
  if (!stop.departedAt) await patchStop(stop.id, { departedAt: left });
  await finishStopDetention(stop.id, left);
  if (step.delivers) await transition(loadId, LoadStatus.delivered, actor);
}

/** Perform the back step (undo / unbook / reopen). */
export async function doBack(loadId: string, step: BackStep, actor: Actor): Promise<void> {
  const load = await freshLoad(loadId);
  switch (step.kind) {
    case "unbook":
    case "reopen":
      await transition(loadId, LoadStatus.considering, actor);
      return;
    case "undo_delivered": {
      await transition(loadId, LoadStatus.rolling, actor);
      if (step.stop != null) {
        const stop = await stopAt(load, step.stop);
        await patchStop(stop.id, { departedAt: null });
        await resumeStopDetention(load, { ...stop, departedAt: null }, actor);
      }
      return;
    }
    case "undo_depart": {
      const stop = await stopAt(load, step.stop);
      await patchStop(stop.id, { departedAt: null });
      await resumeStopDetention(load, { ...stop, departedAt: null }, actor);
      return;
    }
    case "undo_arrive": {
      const stop = await stopAt(load, step.stop);
      await patchStop(stop.id, { arrivedAt: null });
      await dropStopDetention(stop.id);
      return;
    }
  }
}

/** Cancel a booked or rolling load; a clock running for it stops. */
export async function cancelLoad(loadId: string, actor: Actor): Promise<void> {
  await transition(loadId, LoadStatus.cancelled, actor);
  const running = (await getDb().detention.toArray()).filter((d) => d.deletedAt == null && d.loadId === loadId && d.departedAt == null);
  for (const d of running) if (d.loadStopId) await finishStopDetention(d.loadStopId, nowIso());
}

/**
 * POD on paper (no photo): records a POD document without a file so the load
 * can be delivered — e.g. the signed paper POD went to the broker, or this
 * server has no document storage. The server accepts it (storage key is the
 * org-scoped one it would issue) and checks POD the same way.
 */
export async function recordPaperPod(loadId: string, actor: Actor): Promise<void> {
  if (await hasPod(loadId)) return;
  const id = newId();
  const at = nowIso();
  const row: Document = {
    id,
    orgId: actor.org.id,
    version: 0,
    createdAt: at,
    updatedAt: at,
    deletedAt: null,
    loadId,
    expenseId: null,
    kind: DocumentKind.pod,
    storageKey: `orgs/${actor.org.id}/docs/${id}`,
    contentType: PAPER_POD_CONTENT_TYPE,
    bytes: null,
    ocrText: null,
    ocrParsed: null,
  };
  await getSyncClient().mutate({
    entity: "document",
    op: "upsert",
    id,
    baseVersion: null,
    patch: row as unknown as Record<string, unknown>,
    row: row as unknown as Record<string, unknown>,
  });
}

export const PAPER_POD_CONTENT_TYPE = "application/x-haulsheet-paper";

export function isPaperDocument(d: Pick<Document, "contentType">): boolean {
  return d.contentType === PAPER_POD_CONTENT_TYPE;
}

/** Human labels for statuses and stop kinds (no enum names on screen). */
export const STATUS_LABEL: Record<LoadStatus, string> = {
  considering: "Considering",
  passed: "Passed",
  booked: "Booked",
  rolling: "Rolling",
  delivered: "Delivered",
  paid: "Paid",
  cancelled: "Cancelled",
};

export function stopLabel(s: Pick<ProgressStop, "kind" | "seq">, isLastDrop: boolean): string {
  if (s.kind === StopKind.pickup) return "Pickup";
  return isLastDrop ? "Delivery" : `Stop ${s.seq}`;
}
